import React, { useCallback, useMemo, useState } from 'react'
import { useTheme } from '@material-ui/core/styles'
import { useLazyQuery, useMutation } from '@apollo/client'
import { DeleteOwnerResults, VerifyOwnerDeletionResults } from 'api/graphQL/types/project.types'
import { GetOwnerDeletionImpactDataQuery } from 'api/graphQL/queries/project.queries'
import { DeleteOwnerProjectParameterMutation } from 'api/graphQL/mutations/project.mutations'
import ConfirmationDialog from 'components/ConfirmationDialog/ConfirmationDialog'
import useGlobalSnackbar from 'shared/utils/useGlobalSnackbar'

/**
 * Hook result interface for managing owner removal dialog and deletion process
 * @property openRemoveOwnerDialog - Function to initiate the owner removal workflow with optional success callback
 * @property RemoveOwnerConfirmationDialog - Pre-configured JSX element for the confirmation dialog
 */
interface UseRemoveOwnerDialogResult {
    /**
     * Opens the owner removal dialog and initiates impact analysis
     * @param ownerId - ID of the owner to remove
     * @param onSuccessDeletionCallback - Callback executed after successful deletion
     * @param onCancelDeletionCallback - Callback executed when dialog is cancelled
     */
    openRemoveOwnerDialog: (
        ownerId: string, 
        onSuccessDeletionCallback?: () => void,
        onCancelDeletionCallback?: () => void
    ) => void
    RemoveOwnerConfirmationDialog: JSX.Element
}

const dialogHeaderMsg = "Remove Project Parameter"
const dialogConfirmMsg = "Do you want to continue?"

/**
 * Custom hook for managing owner parameter removal workflow
 * Provides impact analysis, user confirmation, and deletion with optional UI update callbacks
 * @returns {UseRemoveOwnerDialogResult} Object containing the dialog trigger function and dialog component
 */
const useRemoveOwnerDialog = (): UseRemoveOwnerDialogResult => {
    const { showGlobalSnackbar } = useGlobalSnackbar()
    const theme = useTheme()

    const [openDialog, setOpenDialog] = useState(false)
    const [dialogBodyMsg, setDialogBodyMsg] = useState<React.ReactNode>(null)
    const [currentOwnerId, setCurrentOwnerId] = useState<string|null>(null)
    const [successDeleteOwnerCallback, setSuccessDeleteOwnerCallback] = useState<(() => void) | null>(null)
    const [cancelDeleteOwnerCallback, setCancelDeleteOwnerCallback] = useState<(() => void) | null>(null)
    const [isDeletionInProgress, setIsDeletionInProgress] = useState(false)

    const renderDialogContent = useCallback((data: VerifyOwnerDeletionResults) => {
        const affectedProjectsCount = data.getOwnerDeletionImpactData.affectedProjectsCount || 0
        const affectedBestPracticesCount = data.getOwnerDeletionImpactData.affectedBestPracticesCount || 0

        const dialogIntroMsg = "You are about to remove the Project Parameter. This action cannot be undone."

        const noProjectsImpactMsg = "This parameter is not used by any projects."
        const projectsImpactMessage = affectedProjectsCount > 0 
            ? `${affectedProjectsCount} projects will lose this parameter.`
            : noProjectsImpactMsg

        const noBestPracticesImpactMsg = "No Best Practices are associated."
        const bestPracticesImpactMsg = affectedBestPracticesCount > 0 
            ? `${affectedBestPracticesCount} associated Best Practices will be deleted.`
            : noBestPracticesImpactMsg

        const reviewProjectsMsg = "To review which projects are affected, filter your Projects table by this parameter before proceeding."
        const safeToRemoveMsg = "You can safely proceed with removal."
        const dialogOutroMsg = affectedProjectsCount > 0 || affectedBestPracticesCount > 0
            ? reviewProjectsMsg
            : safeToRemoveMsg

        return(
            <>
                <p>{dialogIntroMsg}</p>
                <ul>
                    <li><span>{projectsImpactMessage}</span></li>
                    <li><span>{bestPracticesImpactMsg}</span></li>
                </ul>
                <br/>
                <p>{dialogOutroMsg}</p>
            </>
        )
    }, [])

    /**
     * Handles dialog cleanup by resetting all state values
     */
    const handleDialogReset = useCallback(() => {
        setOpenDialog(false)
        setCurrentOwnerId(null)
        setSuccessDeleteOwnerCallback(null)
        setCancelDeleteOwnerCallback(null)
        setDialogBodyMsg(null)
        setIsDeletionInProgress(false)
    }, [])

    /**
     * Handles dialog cancel action
     */
    const handleDialogClose = useCallback(() => {
        handleDialogReset()
        cancelDeleteOwnerCallback?.()
    }, [cancelDeleteOwnerCallback, handleDialogReset])

    const [fetchOwnerDeletionImpact] = useLazyQuery<VerifyOwnerDeletionResults>(
        GetOwnerDeletionImpactDataQuery,
        {
            fetchPolicy: 'no-cache',
            displayName: 'OwnerDeletionImpactDataQuery',
            onCompleted: useCallback(
                (data: VerifyOwnerDeletionResults) => {
                    if(data.getOwnerDeletionImpactData.status === "failed"){
                        showGlobalSnackbar({
                            variables: {
                                message: data.getOwnerDeletionImpactData.message,
                                variant: 'error',
                                id: 'error-delete-owner',
                            }
                        })
                    }
                    else {
                        setDialogBodyMsg(renderDialogContent(data))
                        setOpenDialog(true)
                    } 
                },
                [renderDialogContent, showGlobalSnackbar]
            ),
            onError: useCallback(() => {
                showGlobalSnackbar({
                    variables: {
                        message: `Error retrieving owner affected data`,
                        variant: 'error',
                        id: 'error-delete-owner',
                    }
                })
                setOpenDialog(false)
            },[showGlobalSnackbar])
        }
    )

    const [deleteOwnerApi] = useMutation<DeleteOwnerResults>(
        DeleteOwnerProjectParameterMutation,
        {
            onCompleted: useCallback(
                (data: DeleteOwnerResults) => {
                    if(data.deleteOwnerParameter.status === "failed"){
                        showGlobalSnackbar({
                            variables: {
                                message: data.deleteOwnerParameter.message,
                                variant: 'error',
                                id: 'error-delete-owner',
                            }
                        })
                    }
                    else {
                        successDeleteOwnerCallback?.()
                        showGlobalSnackbar({
                            variables: {
                                message: data.deleteOwnerParameter.message,
                                variant: 'success',
                                id: 'success-delete-owner',
                            }
                        })
                        
                    }                
                },
                [showGlobalSnackbar, successDeleteOwnerCallback]
            ),
            onError: useCallback((err) => {
                showGlobalSnackbar({
                    variables: {
                        message: `Error on deleting owner parameter`,
                        variant: 'error',
                        id: 'error-delete-owner',
                    }
                })
            }, [showGlobalSnackbar])
        }
    )

    const openRemoveOwnerDialog = useCallback( (
        ownerId:string, 
        onSuccessDeletionCallback?: ()=> void, 
        onCancelDeletionCallback?: ()=> void,
    ) => {
        setCurrentOwnerId(ownerId)
        setSuccessDeleteOwnerCallback(() => onSuccessDeletionCallback || null)
        setCancelDeleteOwnerCallback(() => onCancelDeletionCallback || null)
        
        fetchOwnerDeletionImpact({
            variables: {
                ownerId
            }
        })
    }, [fetchOwnerDeletionImpact])

    const handleOwnerParameterDeletion = useCallback(() => {
        if(isDeletionInProgress) return

        setIsDeletionInProgress(true)
        
        deleteOwnerApi({
            variables:{
                ownerId: currentOwnerId
            }
        })
        .finally(() => {
            setIsDeletionInProgress(false)
            handleDialogReset()
        })

    }, [currentOwnerId, deleteOwnerApi, handleDialogReset, isDeletionInProgress])

    const RemoveOwnerConfirmationDialog = useMemo( () => (
        <ConfirmationDialog
            data-testid="removeOwnerConfirmationDialog"
            openDialog={openDialog}
            handleDialogClose={handleDialogClose}
            dialogActionCallback={handleOwnerParameterDeletion}
            dialogHeading={dialogHeaderMsg}
            contentNode={dialogBodyMsg}
            confirmMessage={dialogConfirmMsg}
            size="default"
            dialogStyles={{
                zIndex: theme.zIndex.modal + 2,
            }}
            isConfirmButtonDisabled={isDeletionInProgress}
            contentStyles={{height: 'auto'}}
        />
    ), 
    [
        openDialog, 
        handleDialogClose, 
        dialogBodyMsg, 
        handleOwnerParameterDeletion,
        theme.zIndex.modal,
        isDeletionInProgress
    ])

    return { openRemoveOwnerDialog, RemoveOwnerConfirmationDialog }
}

export default useRemoveOwnerDialog